import allLocales from '~/config/locales.json';
import { actionTypes as navigationActions } from '~/store/navigation';
import CmsService from '~/services/CmsService';
import magazineHelpersMixin from '~/mixins/magazineHelpersMixin';

const NOT_FOUND_ERROR = '404 page not found';

/**
 * A mixin for handling taxonomy pages.
 */
export default {
    mixins: [magazineHelpersMixin],
    data() {
        return {
            taxonomyPage: null,
        };
    },
    head() {
        if (!this.taxonomyPage?.seoMeta) {
            return {};
        }

        const meta = this.generateMeta(this.taxonomyPage.seoMeta, this.baseMetaMapping);

        return {
            title: this.taxonomyPage.seoMeta.og_title,
            meta,
        };
    },
    methods: {
        async getTaxonomyData(context, taxonomyEndpoint) {
            const { slug } = context.params;
            const cmsService = new CmsService(context);

            try {
                const taxonomyPage = await cmsService.getTaxonomyWithSlug(slug, taxonomyEndpoint);

                if (taxonomyPage?.count === 0 && taxonomyEndpoint === 'tags') {
                    return context.redirect(302, context.localePath({ name: 'magazine' }));
                }

                const translatedPages = await cmsService.getTaxonomiesWithIds(
                    Object.values(taxonomyPage.translations),
                    taxonomyEndpoint
                );

                // Setup dynamic language switcher links for articles
                const routeParams = {};
                const translationIdsByLang = taxonomyPage.translations;

                allLocales.forEach(locale => {
                    const lang = new Intl.Locale(locale.iso).language;
                    const translatedPage = translatedPages.find(c => c.id === translationIdsByLang[lang]);

                    if (context.i18n.locale === locale.code) {
                        return;
                    }

                    if (!translatedPage) {
                        // Translation not published for this page, point lang switcher link to Magazine front page
                        routeParams[locale.code] = { name: 'magazine' };

                        return;
                    }

                    routeParams[locale.code] = { slug: translatedPage.slug };
                });

                await context.store.dispatch(navigationActions.SET_ROUTE_PARAMS, { routeParams, i18n: context.i18n });

                return { taxonomyPage };
            } catch (error) {
                if (error?.message === NOT_FOUND_ERROR) {
                    context.app.$sentry.captureException(error);
                }
            }
        },
    },
    computed: {
        showPills() {
            if (!this.taxonomyPage?.subNavigationData) {
                return false;
            }

            return this.taxonomyPage.subNavigationData.enabled && this.taxonomyPage.subNavigationData.links?.length > 0;
        },
        showWpContent() {
            // If URL contains a page query that is larger than 1, return false
            return !this.$route.query.page || this.$route.query.page <= 1;
        },
    },
};
